import MemberSearchModal from '@src/modals/HomeModals/MemberSearchModal/MemberSearchModal';
import MemberSelectModal from '@src/modals/HomeModals/MemberSelectModal/MemberSelectModal';
import RideSearchModal from '@src/modals/HomeModals/RideSearchModal/RideSearchModal';
import SOSearchModal from '@src/modals/HomeModals/SOSearchModal/SOSearchModal';
import ContractorSearchModal from '@src/modals/HomeModals/ContractorSearchModal/ContractorSearchModal';
import { AlertModal } from '@src/provider/AlertProvider';
import { setIsShowRide } from '@src/store/slices/rides/ridesSlice';
import {
  BuildingFilledIcon,
  CarBoldIcon,
  ContractorIcon,
  MemberIcon,
  MonitoringIcon,
  ReportingIcon,
  ScheduledIcon,
  UserIcon,
  UserManagementIcon,
} from '@src/svg';
import { encrypt } from '@src/utils';
import { useAppSelector } from '@store/hooks';
import { authLoginApi } from '@store/rtk/auth';
import { useCreateFromAllyMemberMutation } from '@store/rtk/members';
import { setIsContractorSaved } from '@store/slices/contractors/contractorSlice';
import { setIsPayorSaved } from '@store/slices/payors/info/payorsInfoSlice';
import { selectUsers } from '@store/slices/users/usersSlice';
import SectionTitle from '@uikit/Section/SectionTitle/SectionTitle';
import { openSecureTab, sessionSetter, updateXHeaderId } from '@utils/storage';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import EligibilityHistoryModal from '@src/modals/HomeModals/MemberCreateModal/EligibilityHistoryModal/EligibilityHistoryModal';
import MemberCreateModal from '@src/modals/HomeModals/MemberCreateModal/MemberCreateModal';
import TransactionModal from '@src/modals/HomeModals/MemberCreateModal/TransactionModal/TransactionModal';
import ActionItem from './ActionItem/ActionItem';
import { useLazyGetActiveContractorsQuery } from '@store/rtk/selects';
import { logger } from '@utils/logger';
import { X_HEADER_ID_NAME } from '@utils/constants';
import { selectSelects } from '@store/slices/selects/selects';
import ActionItemSkeleton from '@uikit/Loader/ActionItemSkeleton/ActionItemSkeleton';
import { toastError } from '@utils/helpers';
import { PERMISSION_TITLES } from '@hooks/useDefinePermissions';
import { toastMessage } from '@utils/toastMessage';

const HomeScreen: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const existing: any = sessionSetter.getItem(X_HEADER_ID_NAME) || {};

  const { userInfo, isContractorVersion } = useAppSelector(selectUsers);
  const contractorVersionQuery = useAppSelector((state) =>
    userInfo?.associated
      ? authLoginApi.endpoints.getContractorVersion.select({
          payerId: userInfo.associated,
        })(state)
      : undefined
  );
  const isWaitingForContractorVersion =
    Boolean(userInfo?.associated) &&
    contractorVersionQuery?.data === undefined &&
    contractorVersionQuery?.status !== 'rejected';

  const [
    getActiveContractors,
    {
      isLoading: isActiveContractorsLoading,
      isFetching: isActiveContractorsFetching,
    },
  ] = useLazyGetActiveContractorsQuery();
  const [createAllyMember] = useCreateFromAllyMemberMutation();

  const { activeContractors } = useAppSelector(selectSelects);
  const [isContractorsLoading, setIsContractorsLoading] =
    useState<boolean>(true);

  const [toggleMemberSearchModal, setToggleMemberSearchModal] =
    useState<boolean>(false);
  const [toggleMemberSelectModal, setToggleMemberSelectModal] = useState<{
    toggle: boolean;
    title: string;
    handleCreate: () => void;
  }>({
    toggle: false,
    title: '',
    handleCreate: () => {},
  });
  const [navigateId, setNavigateId] = useState<null | number>(null);
  const [toggleRideSearchModal, setToggleRideSearchModal] =
    useState<boolean>(false);
  const [toggleSOSearchModal, setToggleSOSearchModal] =
    useState<boolean>(false);
  const [toggleContractorSearchModal, setToggleContractorSearchModal] =
    useState<boolean>(false);
  const [toggleMemberCreateModal, setToggleMemberCreateModal] =
    useState<boolean>(false);

  const [toggleNotFoundModal, setToggleNotFoundModal] =
    useState<boolean>(false);

  const [createEligibilityHistoryModal, setCreateEligibilityHistory] =
    useState<boolean>(false);
  const [toggleTransactionModal, setToggleTransactionModal] =
    useState<boolean>(false);

  const [memberData, setMemberData] = useState<any>({});
  const [memberStatus, setMemberStatus] = useState<{
    status: 'CONFLICT' | 'NOT_FOUND' | 'FOUND' | null;
    id: null | number;
  }>({ status: null, id: null });

  const fetchData = useCallback(async () => {
    if (!existing.user && !userInfo) return;

    if (!isContractorVersion) {
      setIsContractorsLoading(true);

      getActiveContractors({})
        .unwrap()
        .catch((error) => {
          logger.log(error);
        })
        .finally(() => {
          setIsContractorsLoading(false);
        });
    } else {
      setIsContractorsLoading(false);
    }
  }, [
    existing.user,
    userInfo?.id,
    isContractorVersion,
    dispatch,
    getActiveContractors,
  ]);

  useEffect(() => {
    if (isContractorVersion || activeContractors !== null) {
      setIsContractorsLoading(false);
    }
  }, [isContractorVersion, activeContractors]);
  useEffect(() => {
    if (!userInfo || isWaitingForContractorVersion) {
      return;
    }

    fetchData();
    document.title = 'Home';
  }, [fetchData, userInfo?.id, isWaitingForContractorVersion]);

  useEffect(() => {
    if (!memberStatus.status) return;

    switch (memberStatus.status) {
      case 'CONFLICT':
        AlertModal.show({
          title: 'Duplicate Member',
          message: 'The member already exists in the system',
          buttons: [
            {
              title: 'Go to Member',
              color: '#4F7EFFFF',
              onClick: () => {
                if (memberStatus.id) {
                  updateXHeaderId('member', memberStatus.id);

                  const encodeId = encrypt({ member: memberStatus.id });
                  window.open(`/members/information/${encodeId}`, '_blank');
                } else {
                  toast.error(toastMessage.MEMBER.memberIdIsMissing);
                }

                handleCloseCreateModal();
              },
            },
          ],
        });
        break;

      case 'NOT_FOUND':
        setToggleNotFoundModal(true);
        handleCloseCreateModal();
        AlertModal.show({
          title: 'Member Not Found',
          message: '',
          extraMessages: [
            `The member could not be found. The member can be added manually to the system.`,
          ],
          buttons: [
            {
              title: 'Create Member',
              color: '#4F7EFFFF',
              onClick: () => {
                setToggleNotFoundModal(false);
                setCreateEligibilityHistory(true);
              },
            },
          ],
        });
        break;

      case 'FOUND':
        AlertModal.show({
          title: 'Member Found',
          message: 'Congratulations',
          extraMessages: ['Are you sure you want to create a member profile?'],
          buttons: [
            {
              title: 'Create Member',
              color: '#4F7EFFFF',
              onClick: async () => {
                try {
                  const result = await createAllyMember({
                    body: memberData,
                  }).unwrap();

                  updateXHeaderId('member', result.id);
                  const encodeId = encrypt({ ['member']: result.id });
                  window.open(`/members/information/${encodeId}`, '_blank');
                  handleCloseCreateModal();
                } catch (error) {
                  toastError(error, toastMessage.MEMBER.failedToCreateMember);
                  logger.error(error);
                }
              },
            },
          ],
        });
        break;
    }
  }, [memberStatus, memberData]);

  const memoizedData = useMemo(() => {
    return (activeContractors || []).map((el, index) => ({
      ...el,
      id: index + 9,
      actionText: el.label,
      actionFunction: () => {
        const encodeId = encrypt({ contractor: el.id });
        dispatch(setIsContractorSaved(true));
        updateXHeaderId('contractor', el.id);
        const existing: any = sessionSetter.getItem(X_HEADER_ID_NAME) || {};
        navigate(`/contractors/information/${encodeId}`, {
          state: {
            xHeader: existing,
          },
        });
      },
    }));
  }, [activeContractors]);

  const actionsHomePage = [
    {
      icon: <MemberIcon />,
      iconText: 'Members',
      module: 'Members',
      actions: [
        {
          id: 1,
          requiredAbility: 'Create',
          actionText: 'Create Member',
          actionFunction: () => setToggleMemberCreateModal(true),
        },
        {
          id: 2,
          requiredAbility: 'Read',
          actionText: 'Search Members',
          actionFunction: () => setToggleMemberSearchModal(true),
        },
      ],
    },
    {
      icon: <CarBoldIcon />,
      iconText: 'Rides',
      module: 'Rides',

      actions: [
        {
          id: 3,
          actionText: 'Create Ride ',
          requiredAbility: 'Create',
          actionFunction: () => {
            dispatch(setIsShowRide(true));
            setToggleMemberSelectModal({
              toggle: true,
              title: 'Create Ride',
              handleCreate: () => {
                const secureData = encrypt(
                  sessionSetter.getItem(X_HEADER_ID_NAME)
                );
                openSecureTab('/rides/information', secureData);
              },
            });
          },
        },
        {
          id: 4,
          requiredAbility: 'Read',
          actionText: 'Search Rides',
          actionFunction: () => setToggleRideSearchModal(true),
        },
      ],
    },
    {
      icon: <ScheduledIcon />,
      iconText: 'Standing Orders',
      module: 'StandingOrder',

      actions: [
        {
          id: 7,
          requiredAbility: 'Create',
          actionText: 'Create S.O.',
          actionFunction: () =>
            setToggleMemberSelectModal({
              toggle: true,
              title: 'Create S.O.',
              handleCreate: () => {
                const secureData = encrypt(
                  sessionSetter.getItem(X_HEADER_ID_NAME)
                );
                openSecureTab('/standing-order/details', secureData);
                dispatch(setIsShowRide(false));
              },
            }),
        },
        {
          id: 8,
          actionText: 'Search S.O.s',
          requiredAbility: 'Read',

          actionFunction: () => setToggleSOSearchModal(true),
        },
      ],
    },
    // TODO: change icon and text to Assignment & Monitoring
    {
      icon: <MonitoringIcon />,
      iconText: isContractorVersion
        ? 'Assignment & Monitoring'
        : 'Ride Monitoring',
      module: 'Monitoring',
      actions: [
        !isContractorVersion
          ? null
          : {
              id: 11,
              actionText: 'Ride Assignment',
              module: 'Assignments',
              actionFunction: () => {
                navigate('/assignment/information/');
              },
            },
        {
          id: 12,
          actionText: 'Ride Monitoring',
          actionFunction: () => {
            navigate('/monitoring/');
          },
        },
      ].filter(Boolean),
    },
    {
      icon: <ContractorIcon />,
      iconText: 'Contractors',
      module: 'Contractors',
      actions: isContractorVersion
        ? [
            {
              id: 1,
              requiredAbility: 'Create',
              permissionTab: PERMISSION_TITLES.INFO,
              actionText: 'Create Contractor',
              actionFunction: () => {
                dispatch(setIsContractorSaved(false));
                updateXHeaderId('contractor', null);
                navigate('/contractors/create');
              },
            },
            {
              id: 2,
              requiredAbility: 'Read',
              actionText: 'Search Contractors',
              actionFunction: () => {
                setToggleContractorSearchModal(true);
              },
            },
          ]
        : memoizedData,
    },
    {
      icon: <ReportingIcon />,
      iconText: 'Reporting Dashboard',
      module: 'Dashboard',
      actions: [
        {
          id: 10,
          actionText: 'Report Dashboard',
          actionFunction: () => {
            navigate('/reporting/ride-reports');
          },
        },
      ],
    },
    {
      icon: <BuildingFilledIcon />,
      iconText: 'Payers',
      module: 'Payors',

      actions: [
        {
          id: 5,
          actionText: 'View',
          actionFunction: () => {
            const encodeId = encrypt({ ['payor']: userInfo?.associated });
            dispatch(setIsPayorSaved(true));
            updateXHeaderId('payor', userInfo?.associated);

            navigate(`/payors/information/${encodeId}`, {
              state: {
                xHeader: existing,
              },
              replace: true,
            });
          },
        },
      ],
    },
    {
      icon: <UserManagementIcon />,
      iconText: 'Users',
      module: 'Admin',

      actions: [
        {
          id: 6,
          actionText: 'Admin Screen',
          actionFunction: () => {
            navigate('/administrator/users');
          },
        },
      ],
    },
    {
      icon: <UserIcon />,
      iconText: 'Profile & Activity',
      actions: [
        {
          id: 13,
          isAlwaysEnabled: true,
          actionText: 'My Profile',
          actionFunction: () => navigate('/profile/user-profile'),
        },
        {
          id: 14,
          isAlwaysEnabled: true,
          actionText: 'Recent Activity',
          actionFunction: () => navigate('/profile/recent-activity'),
        },
      ],
    },
  ];

  const handleCloseModal = () => {
    setToggleMemberSelectModal({
      toggle: false,
      title: '',
      handleCreate: () => {},
    });
    setToggleMemberSearchModal(false);
    setToggleSOSearchModal(false);
    setToggleRideSearchModal(false);
    setToggleContractorSearchModal(false);
    setToggleMemberCreateModal(false);
    setToggleNotFoundModal(false);
    setToggleTransactionModal(false);
  };

  const handleCloseCreateModal = () => {
    setToggleMemberCreateModal(false);
  };

  const handleCloseNotFoundModal = () => {
    setToggleNotFoundModal(false);
  };
  // const fullData = ready.reduce(
  //   (acc, item) => {
  //     acc[item.module] = item.abilities;
  //     return acc;
  //   },
  //   {} as Record<string, any>
  // );

  // const activityItems = recentActivity.content.map((item, index) => {
  //   const moduleAbilities =
  //     fullData[
  //       `${item.model.charAt(0).toUpperCase() + item.model.slice(1)}s` ===
  //       'Users'
  //         ? 'Admin'
  //         : `${item.model.charAt(0).toUpperCase() + item.model.slice(1)}s`
  //     ] || [];

  //   const navigateToPage = (url: string, options?: { state?: unknown }) => {
  //     window.open(url, '_blank');
  //   };

  //   const handleClick = () => {
  //     const existing: any = sessionSetter.getItem(X_HEADER_ID_NAME) || {};
  //     if (!item.data?.id) return;
  //     if (item.model === 'ride') {
  //       updateXHeaderId('member', item.data?.member?.id);
  //       if (
  //         item.resource === 'ride' ||
  //         item.resource === 'standingOrder' ||
  //         item.resource === 'rideSegment'
  //       ) {
  //         const rideSegment =
  //           item.resource === 'rideSegment' ? 'ride' : item.resource;
  //         const encodeId = encrypt({ [`${rideSegment}`]: item.data?.id });
  //         navigateToPage(`${linkTo(rideSegment)}/${encodeId}`);
  //       }
  //     } else if (item.model === 'user') {
  //       navigateToPage(`/administrator/users/`, {
  //         state: { userId: item.data?.id, xHeader: existing },
  //       });
  //     } else {
  //       updateXHeaderId(item.model, item.data?.id);
  //       const encodeId = encrypt({ [item.model]: item.data?.id });
  //       navigateToPage(`${linkTo(item.model)}/${encodeId}`);
  //     }
  //   };

  //   return (
  //     <div
  //       key={index}
  //       onClick={moduleAbilities.length ? handleClick : undefined}
  //     >
  //       <ActivityItem
  //         item={item}
  //         moduleAbilities={moduleAbilities}
  //         dataTestId={`c-home-activity-item-${index}`}
  //       />
  //     </div>
  //   );
  // });

  const actionItems = actionsHomePage.map((item, index) => {
    return (
      <ActionItem
        key={item.iconText || index}
        item={item}
        idx={index}
        isLastItem={index === actionsHomePage.length - 1}
        dataTestId={`action-home-item-${item.iconText}`}
      />
    );
  });

  // const shouldShowButton =
  //   !isFetching && !recentActivity.last && recentActivity.content.length > 0;

  const shouldShowActionsSkeleton =
    isActiveContractorsLoading ||
    isActiveContractorsFetching ||
    isContractorsLoading;

  return (
    <div data-test-id="c-home-screen" className="home-page-wrapper container">
      <div className="home-page-content" data-test-id="c-home-screen-content">
        <div className="home-page-actions-block">
          <SectionTitle
            title="Actions and Search"
            dataTestId="c-home-screen-actions-block"
          />
          <div
            className="home-page-actions"
            data-test-id="c-home-screen-actions"
          >
            {shouldShowActionsSkeleton ? (
              <ActionItemSkeleton dataTestId="c-home-screen-actions-skeleton" />
            ) : (
              actionItems
            )}
          </div>
        </div>
        {/*
        <div
          className="home-page-activity-block"
          data-test-id="c-home-screen-activity-block"
        >
          <SectionTitle
            title="Recent Activity"
            dataTestId="c-home-screen-activity-title"
          />

          <div
            className="home-page-activities"
            data-test-id="c-home-screen-activities"
          >
            <div
              className="home-page-activity"
              ref={listRef}
              data-test-id="c-home-screen-activity"
            >
              {activityItems}
              {isFetching || isLoading ? <ActivitySkeleton /> : null}
            </div>

            {isShowMoreButton && shouldShowButton && (
              <div
                className="home-page-show-more"
                data-test-id="c-home-screen-show-more"
              >
                <button
                  onClick={handleShowMore}
                  className={`${isFetching ? 'disabled' : ''}`}
                  disabled={isFetching}
                  data-test-id="c-home-screen-show-more-btn"
                >
                  Show More
                </button>
              </div>
            )}
          </div>
        </div>
        */}
      </div>

      {toggleMemberSelectModal.toggle && (
        <MemberSelectModal
          setNavigateId={setNavigateId}
          closeModal={handleCloseModal}
          title={toggleMemberSelectModal.title}
          handleCreate={toggleMemberSelectModal.handleCreate}
          onAddNewMember={() => {
            handleCloseModal();
            setToggleMemberCreateModal(true);
          }}
        />
      )}
      {toggleMemberSearchModal && (
        <MemberSearchModal closeModal={handleCloseModal} />
      )}
      {toggleRideSearchModal && (
        <RideSearchModal closeModal={handleCloseModal} />
      )}
      {toggleContractorSearchModal && (
        <ContractorSearchModal closeModal={handleCloseModal} />
      )}
      {toggleSOSearchModal && <SOSearchModal closeModal={handleCloseModal} />}
      {toggleMemberCreateModal && (
        <MemberCreateModal
          setMemberStatus={setMemberStatus}
          closeModal={handleCloseCreateModal}
          setToggleNotFoundModal={setToggleNotFoundModal}
          setMemberData={setMemberData}
        />
      )}
      {createEligibilityHistoryModal && (
        <EligibilityHistoryModal
          createEligibilityHistoryModal={createEligibilityHistoryModal}
          closeModal={setCreateEligibilityHistory}
          memberData={memberData}
          success={(message) => {
            toast.success(message);
          }}
          error={(message) => toast.error(message)}
        />
      )}
      {toggleTransactionModal && (
        <TransactionModal closeModal={handleCloseModal} />
      )}
    </div>
  );
};

export default HomeScreen;
